import { isOfflinePaymentMethod, isOfflinePaymentRecord } from './offlinePaymentMethods';

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const roundCurrency = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

export const donorCoversFees = (value) =>
  value === true ||
  (typeof value === 'string' && ['true', 'yes'].includes(value.trim().toLowerCase()));

export function getPaymentFeePresentation(payment = {}) {
  const deductedFee = roundCurrency(payment.feeAmount ?? payment.fees);
  const charityStackCoveredFee = roundCurrency(payment.charityStackCoveredFeeAmount);
  const displayedFee = roundCurrency(deductedFee + charityStackCoveredFee);
  const coveredByDonor = donorCoversFees(payment.coverFees);

  let paidBy = 'No fee';
  if (charityStackCoveredFee > 0 && deductedFee > 0) {
    paidBy = `${coveredByDonor ? 'Donor' : 'Organization'} and CharityStack`;
  } else if (charityStackCoveredFee > 0) {
    paidBy = 'CharityStack';
  } else if (deductedFee > 0) {
    paidBy = coveredByDonor ? 'Donor' : 'Organization';
  }

  return {
    deductedFee,
    charityStackCoveredFee,
    displayedFee,
    paidBy,
    coverageNote: paidBy === 'No fee' ? 'No processing fee' : `Paid by ${paidBy}`,
  };
}

export function getPaymentEditorSummary({ payment, offline = false, fundsTotal = 0, fees = 0, coverFees = false }) {
  const record = normalizePaymentRecord(payment);
  const feePresentation = offline
    ? getPaymentFeePresentation({ fees: donorCoversFees(coverFees) ? fees : 0, coverFees })
    : getPaymentFeePresentation(record);
  return {
    total: offline
      ? roundCurrency(toNumber(fundsTotal) + (donorCoversFees(coverFees) ? toNumber(fees) : 0))
      : record.totalCharged,
    support: offline ? 0 : record.checkoutBumpAmount,
    discount: offline ? 0 : record.discountAmount,
    ...feePresentation,
  };
}

const cleanValue = (value) => {
  const normalized = String(value ?? '').trim();
  return normalized && normalized.toLowerCase() !== 'none' ? normalized : '';
};

export const PAYMENT_STATUS_VARIANTS = {
  SUCCEEDED: 'success',
  PENDING: 'info',
  FAILED: 'danger',
  ERROR: 'danger',
  FULL_REFUND: 'neutral',
  REFUNDED: 'neutral',
  PARTIAL_REFUND: 'neutral',
  DISPUTED: 'danger',
};

export const formatPaymentStatus = (value) => {
  const normalized = String(value || 'UNKNOWN').toUpperCase();
  return normalized
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const PAYMENT_METHOD_LABELS = {
  CARD: 'Card',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  PAY_BY_BANK: 'ACH',
  VENMO: 'Venmo',
  FREE: 'Free',
};

export const formatPaymentMethod = (value) => {
  const normalized = String(value || 'UNKNOWN').toUpperCase();
  return (
    PAYMENT_METHOD_LABELS[normalized] ||
    normalized
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
};

export { isOfflinePaymentMethod };

export function getPaymentSubscriptionIds(payment = {}) {
  const ids = [
    // A recurring gift's own subscription. The edit drawers price from it (PE-2235).
    payment.subscriptionID,
    ...(payment.fundKeyToSubscriptionID ? Object.values(payment.fundKeyToSubscriptionID) : []),
    ...(Array.isArray(payment.funds) ? payment.funds.map((fund) => fund.subscriptionID) : []),
  ];
  return [...new Set(ids.filter((id) => id && id !== 'EMPTY'))];
}

/**
 * The giving-level card title a donor picked on a crowdfunding form, or ''.
 * It lives on each cart item (`funds[].crowdfundingOption`) and on the record itself.
 * Custom-amount and pre-giving-level donations carry no title, so they show nothing.
 */
export function getGivingLevelTitle(source) {
  const title = source?.crowdfundingOption?.title;
  return typeof title === 'string' ? title.trim() : '';
}

export function getPaymentCartItems(payment = {}) {
  const source = Array.isArray(payment) ? payment[0] || {} : payment || {};
  const funds = Array.isArray(source.funds) ? source.funds : [];
  const tickets = Array.isArray(source.tickets) ? source.tickets : [];

  return [
    ...funds.map((item) => ({ ...item, relationshipType: 'fund' })),
    ...tickets.map((item) => ({ ...item, relationshipType: 'ticket' })),
  ];
}

const getCartItemFormName = (item, fallback = '') =>
  cleanValue(item?.fundraiser || item?.formName || item?.form || fallback);

const getCartItemFormId = (item) => item?.formID || item?.formId || null;

export function getPaymentFormGroups(payment = {}) {
  const source = Array.isArray(payment) ? payment[0] || {} : payment || {};
  const items = getPaymentCartItems(source);
  const fallbackFormName = cleanValue(source.formName);
  const fallbackFormId = source.formID || source.formId || null;

  if (items.length === 0) {
    return [
      {
        key: fallbackFormId ? `id:${fallbackFormId}` : `name:${fallbackFormName || 'unknown'}`,
        formID: fallbackFormId,
        formName: fallbackFormName,
        formType: source.isEvent || source.formType === 'EVENT' ? 'Event' : 'Fundraising',
        items: [],
      },
    ];
  }

  const itemFormNames = items.map((item) => getCartItemFormName(item)).filter(Boolean);
  const distinctFormNames = new Set(itemFormNames.map((name) => name.toLowerCase()));
  const explicitIdsByName = new Map();

  items.forEach((item) => {
    const formName = getCartItemFormName(item);
    const formID = getCartItemFormId(item);
    if (!formName || !formID) return;
    const key = formName.toLowerCase();
    const ids = explicitIdsByName.get(key) || new Set();
    ids.add(formID);
    explicitIdsByName.set(key, ids);
  });

  const groups = new Map();
  items.forEach((item, index) => {
    const formName =
      getCartItemFormName(
        item,
        distinctFormNames.size <= 1 || itemFormNames.length === 0 ? fallbackFormName : '',
      ) || `Form ${index + 1}`;
    const idsForName = explicitIdsByName.get(formName.toLowerCase());
    const inferredFormId = idsForName?.size === 1 ? [...idsForName][0] : null;
    const formID =
      getCartItemFormId(item) ||
      inferredFormId ||
      (distinctFormNames.size <= 1 ? fallbackFormId : null);
    const groupKey = formID ? `id:${formID}` : `name:${formName.toLowerCase()}`;
    const existing = groups.get(groupKey) || {
      key: groupKey,
      formID,
      formName,
      relationshipTypes: new Set(),
      items: [],
    };

    existing.relationshipTypes.add(item.relationshipType);
    existing.items.push(item);
    groups.set(groupKey, existing);
  });

  return [...groups.values()].map(({ relationshipTypes, ...group }) => ({
    ...group,
    formType:
      relationshipTypes.size > 1
        ? 'Fundraising & Event'
        : relationshipTypes.has('ticket')
          ? 'Event'
          : 'Fundraising',
  }));
}

export function buildPaymentEvents(payment = {}) {
  const events = [];
  const state = String(payment.state || payment.status || 'UNKNOWN').toUpperCase();
  events.push({
    key: 'initial',
    state:
      state === 'FAILED' || state === 'ERROR'
        ? 'FAILED'
        : state === 'PENDING'
          ? 'PENDING'
          : 'SUCCEEDED',
    timestamp:
      payment.timestamp || payment.createdAt || payment.transactionDate || payment.dateCreated,
    amount: toNumber(payment.totalCharged ?? payment.amount ?? payment.grossAmount),
  });

  if (Array.isArray(payment.refunded)) {
    payment.refunded.forEach((refund, index) => {
      events.push({
        key: refund.id || `refund-${index}`,
        state: 'REFUNDED',
        timestamp: refund.timestamp || refund.createdAt,
        amount: toNumber(refund.refundAmount),
      });
    });
  }

  return events.sort((left, right) => {
    const leftTime = new Date(left.timestamp || 0).getTime();
    const rightTime = new Date(right.timestamp || 0).getTime();
    return rightTime - leftTime;
  });
}

const normalizeCustomInput = (input, index) => {
  if (!input) return null;
  const label = cleanValue(input.title || input.label || input.name) || `Response ${index + 1}`;
  const answers = Array.isArray(input.answers)
    ? input.answers
    : input.value !== undefined
      ? [input.value]
      : input.answer !== undefined
        ? [input.answer]
        : [];
  const value = answers
    .map((answer) => cleanValue(answer))
    .filter(Boolean)
    .join(', ');
  return value ? { label, value } : null;
};

export function getPaymentResponses(payment = {}) {
  const inputs = [
    ...(Array.isArray(payment.customInputs) ? payment.customInputs : []),
    ...(Array.isArray(payment.funds)
      ? payment.funds.flatMap((fund) =>
          Array.isArray(fund?.customInputs) ? fund.customInputs : [],
        )
      : []),
    ...(Array.isArray(payment.tickets)
      ? payment.tickets.flatMap((ticket) =>
          Array.isArray(ticket?.customInputs) ? ticket.customInputs : [],
        )
      : []),
  ];

  const seen = new Set();
  return inputs.map(normalizeCustomInput).filter((response) => {
    if (!response) return false;
    const key = `${response.label}\u0000${response.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function normalizePaymentRecord(payment = {}) {
  const source = Array.isArray(payment) ? payment[0] || {} : payment || {};
  const {
    paymentProcessor: _paymentProcessor,
    processor: _processor,
    defaultPaymentProcessor: _defaultPaymentProcessor,
    ...adminSafeSource
  } = source;
  const firstName = cleanValue(source.firstName);
  const lastName = cleanValue(source.lastName);
  const contactName =
    cleanValue(
      source.dashboardDonorName || source.donorName || source.contactName || source.name,
    ) ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    cleanValue(source.email || source.donorEmail) ||
    'Unknown contact';
  const state = String(
    source.state || source.transactionStatus || source.status || 'UNKNOWN',
  ).toUpperCase();
  const grossAmount = toNumber(source.grossAmount ?? source.amount);
  const feeAmount = toNumber(source.fees);
  const charityStackCoveredFeeAmount = toNumber(source.charityStackCoveredFeeAmount);
  const discountAmount = toNumber(source.discount);
  const checkoutBumpAmount = toNumber(source.checkoutBumpAmount);
  const refundedAmount = Array.isArray(source.refunded)
    ? source.refunded.reduce((sum, refund) => sum + toNumber(refund.refundAmount), 0)
    : 0;
  const feeRefundedAmount = Array.isArray(source.feeRefunded)
    ? source.feeRefunded.reduce((sum, refund) => sum + toNumber(refund.refundAmount), 0)
    : 0;
  const totalCharged = toNumber(
    source.totalCharged ?? undefined,
    grossAmount + (donorCoversFees(source.coverFees) ? feeAmount : 0),
  );
  const funds = Array.isArray(source.funds) ? source.funds : [];
  const tickets = Array.isArray(source.tickets) ? source.tickets : [];
  const isEvent = source.formType === 'EVENT' || tickets.length > 0;
  const paymentMethod = String(source.paymentMethod || 'UNKNOWN').toUpperCase();
  const cardLastFour =
    source.cardLastFourDigits ||
    source.bankAccountLastFourDigits ||
    source.lastFour ||
    source.last4 ||
    '';

  return {
    ...adminSafeSource,
    id: source.transactionID || source.id || null,
    contactName,
    state,
    statusLabel: formatPaymentStatus(state),
    statusVariant: PAYMENT_STATUS_VARIANTS[state] || 'neutral',
    timestamp:
      source.timestamp || source.createdAt || source.transactionDate || source.dateCreated || null,
    email: cleanValue(source.email || source.donorEmail),
    contactID: source.contactID || source.contactId || null,
    grossAmount,
    feeAmount,
    charityStackCoveredFeeAmount,
    discountAmount,
    checkoutBumpAmount,
    totalCharged,
    refundedAmount: roundCurrency(refundedAmount + feeRefundedAmount),
    remainingAmount: roundCurrency(Math.max(totalCharged - refundedAmount - feeRefundedAmount, 0)),
    coverFees: donorCoversFees(source.coverFees),
    taxDeductible:
      source.taxDeductible === undefined ||
      source.taxDeductible === true ||
      source.taxDeductible === 'true',
    paymentMethod,
    paymentMethodLabel: formatPaymentMethod(paymentMethod),
    cardBrand: cleanValue(source.cardBrand),
    cardLastFour,
    cardType: cleanValue(source.cardType || source.cardFunding),
    cardExpiration:
      source.cardExpirationDate ||
      source.cardExpiration ||
      source.expirationDate ||
      [source.cardExpirationMonth, source.cardExpirationYear].filter(Boolean).join('/') ||
      '',
    funds,
    tickets,
    isEvent,
    frequency: String(
      source.donationFrequency ||
        source.frequency ||
        funds[0]?.frequency ||
        (isEvent ? 'ONE_TIME' : 'ONE_TIME'),
    ).toUpperCase(),
    formName:
      cleanValue(source.formName) ||
      cleanValue(funds[0]?.fundraiser) ||
      cleanValue(tickets[0]?.formName),
    formID: source.formID || funds[0]?.formID || tickets[0]?.formID || null,
    subscriptionIDs: getPaymentSubscriptionIds(source),
    events: buildPaymentEvents(source),
    responses: getPaymentResponses(source),
    dedication: {
      type: cleanValue(source.dedicationType || source.tributeType),
      name: cleanValue(source.dedicationName || source.tributeName),
      message: cleanValue(source.dedicationMessage || source.tributeMessage),
    },
    // Decided from the raw source: the processor fields are stripped above, and a
    // gift processed through PayPal also has paymentMethod PAYPAL or VENMO.
    isOffline: isOfflinePaymentRecord(source),
  };
}
